import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import type { Resident, ResidentNameFields } from '@/lib/types'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * All resident statuses that represent a physically-present occupant.
 * Used by resident-side reports so a hospitalized or quarantined resident
 * (still a live tenant) shows up in allergies, pets, emergency, and profile
 * printouts alongside active/away/sick residents.
 */
export const OCCUPIED_RESIDENT_STATUSES = [
  "active",
  "away",
  "sick",
  "hospital",
  "quarantine",
] as const

/**
 * Format a resident's display name, falling back through preferred → first → legal.
 * Returns "Unnamed resident" if no name data is present.
 */
export function formatResidentName(r: Pick<Resident, "preferred_name" | "first_name" | "legal_first_name" | "last_name" | "legal_last_name">): string {
  const first = r.preferred_name || r.first_name || r.legal_first_name || ""
  const last = r.last_name || r.legal_last_name || ""
  const name = `${first} ${last}`.trim()
  return name || "Unnamed resident"
}

/**
 * Format a resident's LEGAL name for documents where the preferred name is
 * wrong (insurance, incident reports for authorities): legal fields first,
 * display fields as fallback, never preferred_name.
 */
export function formatResidentLegalName(r: Pick<Resident, "first_name" | "legal_first_name" | "last_name" | "legal_last_name">): string {
  const first = r.legal_first_name || r.first_name || ""
  const last = r.legal_last_name || r.last_name || ""
  const name = `${first} ${last}`.trim()
  return name || "Unnamed resident"
}

/**
 * Lower-cased search haystack for resident pickers: display name, legal name
 * and suite. Every hand-rolled `\`${r.first_name} ${r.last_name}\`.toLowerCase()`
 * predicate (14 of them on 2026-09-07) missed `preferred_name`, so a resident
 * known as Peggy could not be found by typing Peggy. One haystack, both names.
 * Guarded to zero inline copies by tests/unit/helper-adoption-guards.test.ts.
 */
export function residentSearchText(
  r: Pick<Resident, "preferred_name" | "first_name" | "legal_first_name" | "last_name" | "legal_last_name" | "suite_number">,
): string {
  return [formatResidentName(r), formatResidentLegalName(r), r.suite_number ?? ""]
    .join(" ")
    .toLowerCase()
}

/**
 * Normalise any fetched resident row into the six fields the name helpers
 * need: `ResidentsDocument` (types/appwrite) marks them optional, `Resident`
 * (lib/types) marks them nullable, and picker options used to copy two of the
 * six by hand. This is the one way to build a picker option from a document —
 * copying fields by hand is how `preferred_name` went missing from six forms.
 */
export function residentNameFields(r: {
  first_name?: string | null
  last_name?: string | null
  preferred_name?: string | null
  legal_first_name?: string | null
  legal_last_name?: string | null
  suite_number?: string | null
}): ResidentNameFields {
  return {
    first_name: r.first_name ?? "",
    last_name: r.last_name ?? "",
    preferred_name: r.preferred_name ?? null,
    legal_first_name: r.legal_first_name ?? null,
    legal_last_name: r.legal_last_name ?? null,
    suite_number: r.suite_number ?? "",
  }
}

/**
 * Format a phone number for display: "4035551234" → "(403) 555-1234"
 * Handles raw digits, already-formatted strings, or partial numbers.
 * Returns the original string if it doesn't look like a phone number.
 */
export function formatPhoneDisplay(value: string | null | undefined): string {
  if (!value) return ""
  const digits = value.replace(/\D/g, "")
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  if (digits.length === 11 && digits[0] === "1") {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  }
  return value
}

/**
 * Sort menu items so that the "Combo Plate" item always appears at position 5 (index 4).
 * CEO requirement: "Combo plate always 5th in server UI and in-suite dining."
 * Other items retain their original order. If fewer than 5 items, combo goes last.
 */
export function sortWithComboFifth<T extends { name: string }>(items: T[]): T[] {
  const combo = items.find(i => i.name.toLowerCase().includes('combo'))
  const others = items.filter(i => !i.name.toLowerCase().includes('combo'))
  if (!combo) return items
  const insertAt = Math.min(4, others.length) // index 4 = 5th position, or last if fewer
  const result = [...others]
  result.splice(insertAt, 0, combo)
  return result
}

/**
 * Safely parse meal_selections from suite service orders into a human-readable string.
 *
 * Handles multiple formats produced by different parts of the app:
 * - Plain text string (from tea-service): "Morning Tea: Coffee, Tea"
 * - JSON object with items[] (from suite-service-interface): {items: [{name: "..."}, ...], sides: [...]}
 * - JSON object with order_details (from in-suite-dining-form): {service_type: "lunch", order_details: {...}}
 * - Double-stringified JSON (legacy bug): a JSON string that when parsed yields another JSON string
 *
 * Returns a display-friendly string in all cases.
 */
export function parseMealSelections(raw: string | null | undefined): string {
  if (!raw) return "-"

  // Try to parse as JSON (may be stringified object or double-stringified)
  let parsed: unknown = raw
  let parseAttempts = 0

  // Unwrap up to 2 levels of JSON stringification (handles double-encoded values)
  while (typeof parsed === "string" && parseAttempts < 2) {
    try {
      const candidate = JSON.parse(parsed)
      // If parsing a string yields another primitive string that looks like plain text, stop
      if (typeof candidate === "string" && parseAttempts > 0) {
        parsed = candidate
        break
      }
      parsed = candidate
      parseAttempts++
    } catch {
      // Not valid JSON - treat as plain text
      break
    }
  }

  // If we still have a string after parsing attempts, return it as-is (plain text format)
  if (typeof parsed === "string") {
    return parsed
  }

  // Handle object formats
  if (parsed && typeof parsed === "object") {
    const obj = parsed as Record<string, unknown>

    // Format: { items: [{ name: "..." }, ...], sides: [...], dessert: "..." }
    // (from suite-service-interface.tsx)
    if (Array.isArray(obj.items)) {
      const parts: string[] = []
      const items = obj.items as Array<{ name?: string; menuItemName?: string }>
      const itemNames = items.map(i => i.name || i.menuItemName).filter(Boolean)
      if (itemNames.length > 0) parts.push(itemNames.join(", "))

      if (Array.isArray(obj.sides) && obj.sides.length > 0) {
        parts.push(`Sides: ${(obj.sides as string[]).join(", ")}`)
      }
      if (obj.salad_dressing) parts.push(`Dressing: ${obj.salad_dressing}`)
      if (obj.dessert) parts.push(`Dessert: ${obj.dessert}`)
      return parts.join(" | ") || raw
    }

    // Format: { service_type: "lunch", order_details: { menuItemName: "...", portionSize: "..." } }
    // (from in-suite-dining-form.tsx)
    if (obj.order_details) {
      const details = obj.order_details as Record<string, unknown>
      const parts: string[] = []
      if (details.menuItemName) parts.push(String(details.menuItemName))
      if (details.portionSize && details.portionSize !== "regular") {
        parts.push(`(${details.portionSize} portion)`)
      }
      if (details.specialRequests) parts.push(String(details.specialRequests))
      return parts.join(" ") || String(obj.service_type || raw)
    }

    // Generic object: try to extract meaningful text
    if (obj.service_type) return String(obj.service_type)
  }

  // Fallback: return raw string
  return raw
}
